import React, { useCallback, useMemo } from "react";
import { Select } from "components/Select/Select";
import { TooltipIcon } from "components/InputCards/TooltipIcon";
import { useTranslate } from "hooks";
import type {
  OrderCodePartOptionStrict,
  OrderCodePartStrict,
} from "stores/product";
import type { OnChange } from "../OrderCodePartOption";

type RangeSelectInputComponentProps = {
  options: number[];
  value: number;
  onChange: OnChange;
  label?: string;
  description?: string;
  disabled?: boolean;
  min: number;
  max: number;
  testId?: string;
};

const RangeSelectInputComponent = ({
  options,
  value,
  onChange,
  label,
  description,
  disabled,
  min,
  max,
  testId,
}: RangeSelectInputComponentProps) => {
  const translate = useTranslate();

  const handleValidate = useCallback(
    (val: string) => {
      const numberVal = Number(val);
      return !(isNaN(numberVal) || numberVal < min || numberVal > max);
    },
    [max, min],
  );

  const optionsMemo = useMemo(
    () => options.map((opt) => ({ value: opt, label: opt })),
    [options],
  );

  return (
    <>
      <TooltipIcon description={description} />
      {label && <h5 className="">{label}</h5>}
      <div className="d-flex flex-nowrap align-items-center w-100">
        <Select
          isClearable={false}
          options={optionsMemo}
          value={optionsMemo.find((opt) => opt.value === value)}
          onChange={(selected: { value: number; label: number } | null) =>
            onChange(selected?.value ?? null)
          }
          isDisabled={disabled}
          formatCreateLabel={(val: string) => `${translate("CREATE")} "${val}"`}
          isValidNewOption={handleValidate}
          className="flex-grow-1"
          isCreatable={true}
          testId={testId}
        />
        <span
          className="ms-2 font-size-sm text-gray-500 text-end"
          style={{ minWidth: "70px" }}
        >
          {min} ... {max}
        </span>
      </div>
    </>
  );
};

export const RangeSelectInput = ({
  option,
  disabled,
  onChange,
  part,
}: {
  option: OrderCodePartOptionStrict;
  disabled: boolean;
  onChange: OnChange;
  part: OrderCodePartStrict;
}) => {
  const optionsMemo = useMemo(
    () =>
      option.signatures
        ?.filter((signature) => signature.state === 0)
        .map((signature) => Number(signature.value)) || [],
    [option.signatures],
  );

  if (
    !option.range ||
    option?.range?.min === null ||
    option?.range?.max === null
  ) {
    console.error("Option has invalid range data:", option);
    return <></>;
  }

  return (
    <RangeSelectInputComponent
      options={optionsMemo}
      onChange={onChange}
      label={option.title ?? undefined}
      value={Number(option.currentSignature?.value)}
      disabled={disabled}
      min={option.range.min}
      max={option.range.max}
      testId={`range-select-input-${part.key}-${option.key}`}
    />
  );
};
